import { useState } from "react";
import { ChevronDown, FileText } from "lucide-react";
import { MarketingWorkFile } from "@/bryntum-lib/models/MarketingWorkFile";
import type { MarketingDocumentFormat } from "@/bryntum-lib/models/MarketingDocument";
import { NamePromptModal } from "@/react/components/NamePromptModal";
import { Button } from "@/modern/components/ui/button-shadcn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/modern/components/ui/dropdown-menu";

interface GenerateMarketingDocumentActionProps {
  file: MarketingWorkFile;
  generating: boolean;
  onGenerate: (format: MarketingDocumentFormat, name: string) => boolean | Promise<boolean>;
}

const FORMAT_OPTIONS: { format: MarketingDocumentFormat; label: string }[] = [
  { format: "docx", label: "Word (DOCX)" },
  { format: "pdf", label: "PDF" },
];

export function GenerateMarketingDocumentAction({
  file,
  generating,
  onGenerate,
}: GenerateMarketingDocumentActionProps) {
  const disabled = file.archived || generating;
  const [format, setFormat] = useState<MarketingDocumentFormat | null>(null);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 min-h-8 shrink-0 gap-1 px-3 text-xs"
            disabled={disabled}
            aria-haspopup="menu"
            title={
              file.archived
                ? "Unarchive this work file to generate a document."
                : "Choose DOCX or PDF format"
            }
          >
            <FileText className="size-3.5" aria-hidden />
            {generating ? "Generating…" : "Generate document"}
            {!generating && <ChevronDown className="size-3.5 opacity-70" aria-hidden />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[10rem]">
          {FORMAT_OPTIONS.map(({ format: optionFormat, label }) => (
            <DropdownMenuItem
              key={optionFormat}
              disabled={disabled}
              onSelect={() => setFormat(optionFormat)}
            >
              {label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <NamePromptModal
        open={format != null}
        onOpenChange={(open) => {
          if (!open) setFormat(null);
        }}
        title="Name this document"
        description="Used as the document title and the download filename."
        fieldLabel="Document name"
        placeholder="e.g. Storm damage case study"
        initialValue={file.displayName}
        submitLabel={format ? `Generate ${format.toUpperCase()}` : "Generate"}
        submittingLabel="Starting…"
        onConfirm={async (name) => {
          if (!format) return;
          const ok = await onGenerate(format, name);
          if (!ok) {
            throw new Error("Document generation did not start");
          }
        }}
      />
    </>
  );
}
